import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output } from '@angular/core';

/**
 * Partner logos are served from Voyado as `{partnerId}Logo.{ext}`. The matching
 * cover photo, where one exists, is `{partnerId}Situasjonsbilde.{ext}` in the
 * same folder. The benefits API carries no cover-photo field, so the URL is
 * derived from urlLogo.
 */
const VOYADO_LOGO_URL = /^(https?:\/\/voyado\.oculos\.no\/bbl\/pictures\/.+?)logo(\.[a-z0-9]+)$/i;

/**
 * Returns the cover-photo URL for a Voyado-hosted logo URL, or null when the
 * logo is missing, not a string, or hosted elsewhere. The caller renders the
 * logo-only card variant when this returns null. A query string or fragment on
 * the logo URL is dropped before matching and is not carried into the cover URL.
 */
export function deriveCoverUrl(urlLogo: unknown): string | null {
  if (typeof urlLogo !== 'string') {
    return null;
  }
  const path = urlLogo.trim().split(/[?#]/)[0];
  const match = VOYADO_LOGO_URL.exec(path);
  return match ? `${match[1]}Situasjonsbilde${match[2]}` : null;
}

/** Voyado's partner ids, and the only ids our own image endpoint answers (WO-21). */
const PARTNER_ID = /^\d{1,8}$/;

/**
 * Returns the Voyado partner id a logo URL belongs to, or null when the URL is
 * not a Voyado logo or its file name does not begin with a partner id. The id is
 * the file-name stem in front of `Logo` and is the key the resolved cover
 * extension is remembered under.
 *
 * A stem that is not a partner id gives null, so the card starts on the direct
 * Voyado URLs instead of asking our endpoint for an id it refuses.
 */
export function partnerIdFromLogoUrl(urlLogo: unknown): string | null {
  if (typeof urlLogo !== 'string') {
    return null;
  }
  const path = urlLogo.trim().split(/[?#]/)[0];
  const match = VOYADO_LOGO_URL.exec(path);
  if (!match) {
    return null;
  }
  const stem = match[1].split('/').pop() || '';
  return PARTNER_ID.test(stem) ? stem : null;
}

const SWAPPABLE_EXTENSIONS: { [ext: string]: string } = { jpg: 'png', png: 'jpg' };

/**
 * Voyado does not always store the cover under the same extension as the logo:
 * partner 2571 has `2571Logo.jpg` beside `2571Situasjonsbilde.png`. When the
 * derived URL 404s, the card retries once with the other extension. Returns
 * null for anything but jpg and png, so those go straight to logo-only.
 */
export function alternateCoverUrl(coverUrl: string | null | undefined): string | null {
  if (typeof coverUrl !== 'string') {
    return null;
  }
  const match = /^(.*\.)([A-Za-z0-9]+)$/.exec(coverUrl);
  if (!match) {
    return null;
  }
  const extension = match[2];
  const swapped = SWAPPABLE_EXTENSIONS[extension.toLowerCase()];
  if (!swapped) {
    return null;
  }
  // Keep the casing of the extension that was there.
  return match[1] + (extension === extension.toUpperCase() ? swapped.toUpperCase() : swapped);
}

/**
 * The host's own cache endpoint. It fetches from Voyado once, keeps the bytes on
 * disk and remembers which extension exists, including that a partner has none,
 * so the browser never probes and never waits on Voyado. The size is decided by
 * the kind on the server, so there is nothing to pass here: a cover comes back at
 * 500 px and a logo at 176, which is its 88 px box at 2x.
 */
export function partnerImageUrl(partnerId: string, kind: 'cover' | 'logo'): string {
  return '/img/' + encodeURIComponent(partnerId) + '/' + kind;
}

const WEB_SHOP_LABEL = 'Nettbutikk';

/**
 * The benefits API returns HTML, so name and teaser can carry entities such as
 * &oslash;. Those render correctly through [innerHTML], but plain-text uses —
 * the modal's call-to-action label, for one — have to decode them first.
 */
export function decodeHtml(value: unknown): string {
  if (typeof value !== 'string') {
    return '';
  }
  if (value.indexOf('&') === -1) {
    return value;
  }
  const holder = document.createElement('textarea');
  holder.innerHTML = value;
  return holder.value;
}

function trimmed(value: unknown): string {
  return typeof value === 'string' ? value.trim() : '';
}

/**
 * True when the image that loaded is the endpoint's "no image" pixel. The header
 * that names it, X-Partner-Image, is not readable from an img element, so the
 * card reads the size the pixel is served at.
 */
function isPlaceholder(event: Event): boolean {
  const image = event?.target as HTMLImageElement | null;
  return !!image && image.naturalWidth === 1 && image.naturalHeight === 1;
}

@Component({
  selector: 'app-benefit-card',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './benefit-card.component.html',
  styleUrls: ['./benefit-card.component.scss'],
  host: { role: 'listitem' }
})
export class BenefitCardComponent {
  @Output() open = new EventEmitter<any>();

  coverUrl: string | null = null;
  logoUrl: string | null = null;
  logoFailed = false;

  private currentBenefit: any;
  private partnerId: string | null = null;
  private coverRetried = false;
  private coverFellBack = false;
  private logoFellBack = false;

  @Input()
  set benefit(value: any) {
    this.currentBenefit = value;
    this.partnerId = partnerIdFromLogoUrl(value?.urlLogo);

    const directLogo = typeof value?.urlLogo === 'string' && value.urlLogo ? value.urlLogo : null;
    this.coverUrl = this.partnerId ? partnerImageUrl(this.partnerId, 'cover') : deriveCoverUrl(value?.urlLogo);
    this.logoUrl = this.partnerId ? partnerImageUrl(this.partnerId, 'logo') : directLogo;

    // A partner outside Voyado has no cache entry, so it is already on its fallback.
    this.coverFellBack = !this.partnerId;
    this.logoFellBack = !this.partnerId;
    this.coverRetried = false;
    this.logoFailed = false;
  }

  get benefit(): any {
    return this.currentBenefit;
  }

  /** The figure is dropped altogether when there is no image to put in it. */
  get hasImagery(): boolean {
    return !!(this.coverUrl || (this.logoUrl && !this.logoFailed));
  }

  /** "Nettbutikk" is a channel, not a discount, so it gets the shop icon. */
  get isWebShop(): boolean {
    return this.benefit?.discountText === WEB_SHOP_LABEL;
  }

  /**
   * Some partners carry the same sentence in teaser and discountText. The badge
   * is dropped in that case rather than printing it twice.
   */
  get showDiscountBadge(): boolean {
    const discount = trimmed(this.benefit?.discountText);
    return !!discount && discount !== trimmed(this.benefit?.teaser);
  }

  /**
   * A partner with no cover is a 200 carrying a 1 by 1 transparent pixel, so the
   * card can tell it from a failure and stops there: no Voyado request, on this
   * view or any later one, since the browser keeps the answer for the day.
   * (WO-22)
   */
  onCoverLoad(event: Event): void {
    if (isPlaceholder(event)) {
      this.coverUrl = null;
    }
  }

  /** The same answer for a logo, which leaves the card with no imagery at all. */
  onLogoLoad(event: Event): void {
    if (isPlaceholder(event)) {
      this.logoFailed = true;
    }
  }

  /**
   * An error now means the endpoint itself failed: Voyado unreachable with no copy
   * held (502), a caller over the miss limit (429), or a network failure. The card
   * tries Voyado directly once, then the other extension, then gives up and shows
   * the logo-only variant. An error for a URL the card has already moved on from is
   * ignored, so a late failure cannot discard a later attempt.
   */
  onCoverError(event?: Event): void {
    const failed = (event?.target as HTMLImageElement | null)?.getAttribute('src');
    if (failed && failed !== this.coverUrl) {
      return;
    }

    if (!this.coverFellBack) {
      this.coverFellBack = true;
      this.coverUrl = deriveCoverUrl(this.benefit?.urlLogo);
      if (this.coverUrl) {
        return;
      }
    }

    if (!this.coverRetried) {
      const alternate = alternateCoverUrl(this.coverUrl);
      this.coverRetried = true;
      if (alternate) {
        this.coverUrl = alternate;
        return;
      }
    }

    this.coverUrl = null;
  }

  /** Same fallback for the logo, which has no second extension to try. */
  onLogoError(): void {
    if (!this.logoFellBack) {
      this.logoFellBack = true;
      const direct = typeof this.benefit?.urlLogo === 'string' && this.benefit.urlLogo ? this.benefit.urlLogo : null;
      if (direct && direct !== this.logoUrl) {
        this.logoUrl = direct;
        return;
      }
    }
    this.logoFailed = true;
  }

  onClick(): void {
    this.open.emit(this.benefit);
  }

  /**
   * Enter acts on keydown and Space on keyup, as a native button does. Space is
   * still prevented on keydown so the page does not scroll while it is held.
   */
  onKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter') {
      event.preventDefault();
      this.open.emit(this.benefit);
      return;
    }
    if (isSpace(event)) {
      event.preventDefault();
    }
  }

  onKeyup(event: KeyboardEvent): void {
    if (isSpace(event)) {
      event.preventDefault();
      this.open.emit(this.benefit);
    }
  }
}

function isSpace(event: KeyboardEvent): boolean {
  return event.key === ' ' || event.key === 'Spacebar';
}
